import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, NotebookPen } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import {
  addCustomerNote,
  getCustomerNotes,
  previewNoteTags,
  type CustomerNote,
} from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { fmtDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { NoteBody } from '@/components/data/NoteBody'

// ─── Client notes, from the booking sheet ──────────────────────────────────────
//
// Requested 2026-08-17: "Show the client's last entered note. Click to enter new
// note after the latest note. Note written in one page with date and note order,
// show the latest first followed new note. Ability to scroll back to see other
// note."
//
// So: ONE page, latest note first, the composer directly after it, earlier notes
// scrolling on below. Dates on every note.
//
// SAVING KEEPS THE TWO-STEP RULE. Tags are AI-suggested and must be
// operator-confirmed before anything is stored — the same
// preview_note_tags → add_customer_note flow chat uses, against the same
// endpoints. Save here asks for the tags first, shows them as toggleable chips,
// and only the second press writes. A note saved from a booking carries that
// booking_id, so it is linked to the session it was written at.
//
// This is a DELIVERY SURFACE over existing capability (list_customer_notes,
// preview_note_tags, add_customer_note all exist in chat and MCP) — no parity or
// InfoPanel change rides with it.

const PAGE = 20

export interface ClientNotesDialogProps {
  customer: { id: string; name: string } | null
  /** Links a note saved here to the session the sheet was opened from. */
  bookingId?: string
  onOpenChange: (open: boolean) => void
}

export function ClientNotesDialog({ customer, bookingId, onOpenChange }: ClientNotesDialogProps) {
  const open = customer !== null

  const [notes, setNotes] = useState<CustomerNote[] | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [draft, setDraft] = useState('')
  // null = not previewed yet; the save button's two lives.
  const [suggestedTags, setSuggestedTags] = useState<string[] | null>(null)
  const [chosenTags, setChosenTags] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !customer) return
    let live = true
    setNotes(null)
    setTotal(null)
    setLoadError(null)
    setDraft('')
    setSuggestedTags(null)
    setChosenTags(new Set())
    setError(null)
    setBusy(false)
    setLoading(true)
    getCustomerNotes(customer.id, { limit: PAGE })
      .then((page) => {
        if (!live) return
        setNotes(page.items)
        setTotal(page.total)
      })
      .catch((e) => { if (live) setLoadError(readApiError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, customer?.id])

  async function loadMore() {
    if (!customer || notes === null) return
    try {
      const page = await getCustomerNotes(customer.id, { limit: PAGE, offset: notes.length })
      setNotes([...notes, ...page.items])
      setTotal(page.total)
    } catch (e) {
      setLoadError(readApiError(e))
    }
  }

  // Step 1 — ask for tags. Nothing is stored.
  async function preview() {
    if (!customer || busy || !draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      const res = await previewNoteTags(customer.id, draft.trim())
      setSuggestedTags(res.suggested_tags)
      setChosenTags(new Set(res.suggested_tags))
    } catch (e) {
      setError(readApiError(e))
    } finally {
      setBusy(false)
    }
  }

  // Step 2 — save with the tags the operator confirmed.
  async function save() {
    if (!customer || busy || !draft.trim()) return
    setBusy(true)
    setError(null)
    try {
      const saved = await addCustomerNote(customer.id, {
        note: draft.trim(),
        tags: [...chosenTags],
        ...(bookingId ? { booking_id: bookingId } : {}),
      })
      setNotes([saved, ...(notes ?? [])])
      setTotal((t) => (t === null ? t : t + 1))
      setDraft('')
      setSuggestedTags(null)
      setChosenTags(new Set())
      toast.success('Note saved', {
        description: bookingId ? 'Linked to this appointment.' : undefined,
      })
    } catch (e) {
      setError(readApiError(e))
    } finally {
      setBusy(false)
    }
  }

  function toggleTag(tag: string) {
    setChosenTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  const latest = notes?.[0] ?? null
  const earlier = notes?.slice(1) ?? []
  const hasMore = total !== null && notes !== null && notes.length < total

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="flex max-h-[85vh] max-w-md flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Client notes</DialogTitle>
          <DialogDescription>{customer?.name ?? ''}</DialogDescription>
        </DialogHeader>

        {customer ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-4 pb-1">
              {loading ? (
                <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Loading notes…
                </div>
              ) : loadError ? (
                <p className="text-[12.5px] text-destructive">{loadError}</p>
              ) : (
                <>
                  {/* The latest note first… */}
                  {latest ? (
                    <div className="flex flex-col gap-1.5">
                      <SectionLabel>Latest note</SectionLabel>
                      <NoteCard note={latest} highlight />
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
                      No notes yet — the one below will be the first.
                    </p>
                  )}

                  {/* …the new note directly after it, as asked… */}
                  <div className="flex flex-col gap-1.5">
                    <SectionLabel>New note</SectionLabel>
                    <textarea
                      value={draft}
                      onChange={(e) => {
                        setDraft(e.target.value)
                        // Edited text invalidates previewed tags — they were
                        // suggested for the old wording.
                        if (suggestedTags) { setSuggestedTags(null); setChosenTags(new Set()) }
                      }}
                      rows={3}
                      placeholder="Write the note…"
                      className="w-full resize-y rounded-lg border border-border bg-card px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring"
                    />

                    {suggestedTags !== null ? (
                      <div className="flex flex-col gap-1.5">
                        <p className="text-[11.5px] text-muted-foreground">
                          Suggested tags — tap to keep or drop, then save:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {suggestedTags.length === 0 ? (
                            <span className="text-[11.5px] text-muted-foreground">No tags suggested.</span>
                          ) : suggestedTags.map((tag) => {
                            const on = chosenTags.has(tag)
                            return (
                              <button
                                key={tag}
                                type="button"
                                role="checkbox"
                                aria-checked={on}
                                onClick={() => toggleTag(tag)}
                                className={cn(
                                  'rounded-full border px-2.5 py-0.5 text-[11.5px] transition-colors',
                                  on
                                    ? 'border-primary bg-primary/10 text-foreground'
                                    : 'border-border bg-card text-muted-foreground line-through',
                                )}
                              >
                                {tag}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ) : null}

                    {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

                    <ConfirmButton
                      // Two lives on purpose: the first press only fetches the
                      // suggested tags (nothing stored), the second one saves
                      // with whatever the operator confirmed.
                      label={suggestedTags === null ? 'Suggest tags & review' : 'Save note'}
                      icon={<NotebookPen className="size-4" aria-hidden />}
                      busy={busy}
                      disabled={!draft.trim()}
                      onClick={() => void (suggestedTags === null ? preview() : save())}
                    />
                  </div>

                  {/* …then the history, scrolling back. */}
                  {earlier.length > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      <SectionLabel>Earlier notes</SectionLabel>
                      {earlier.map((note) => (
                        <NoteCard key={note.id} note={note} />
                      ))}
                    </div>
                  ) : null}

                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() => void loadMore()}
                      className="self-start text-[12px] font-semibold text-primary hover:underline"
                    >
                      Show earlier notes ({(total ?? 0) - (notes?.length ?? 0)} more)
                    </button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function NoteCard({ note, highlight }: { note: CustomerNote; highlight?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2',
        highlight ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
      )}
    >
      <NoteBody text={note.note} />
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[11px] text-muted-foreground">{fmtDateTime(note.created_at)}</span>
        {note.tags.map((tag) => (
          <span key={tag} className="rounded-full bg-muted px-2 py-px text-[10.5px] text-muted-foreground">
            {tag}
          </span>
        ))}
      </div>
    </div>
  )
}
